import React from "react"
import {
  Box, Divider, Flex, IconButton, Text,
} from "@chakra-ui/react"
import Icon from "components/elements/Icon"
import { Filter } from "sharedTypes"
import { FilterFieldConfig, FilterOptionsContext } from "./types"
import FilterChipOperator from "./FilterChipOperator"
import FilterChipValue from "./FilterChipValue"

// Visual variant for the chip. `md` is the dense table-view default; `lg`
// is used in the SegmentForm modal where the chip is the primary editable
// content and needs to read as a form control rather than a header pill.
// Imported by FilterChipOperator + FilterChipValue (no cycle — neither
// imports back from this module).
export type ChipSize = "md" | "lg"

export const CHIP_SIZES = {
  md: {
    h: 9,
    iconSize: 3.5,
    fontSize: "sm" as const,
    px: 3,
    labelGap: 2,
    removeBtnSize: "sm" as const,
    removeIconSize: 3,
    valueMaxW: "200px",
    valueIconSize: 3,
    valueDisplayGap: 1.5,
    labelMaxW: "220px",
  },
  lg: {
    h: 10,
    iconSize: 4,
    fontSize: "md" as const,
    px: 3.5,
    labelGap: 2,
    removeBtnSize: "sm" as const,
    removeIconSize: 3,
    // Only used in the narrower SegmentForm modal — smaller than `md`'s
    // budget so label + value + icon/operator/button chrome comfortably fits.
    valueMaxW: "140px",
    valueIconSize: 3.5,
    valueDisplayGap: 2,
    labelMaxW: "170px",
  },
} as const

type Props = {
  filter: Filter
  config: FilterFieldConfig
  context: FilterOptionsContext
  size?: ChipSize
  onChange: (patch: Partial<Filter>) => void
  onRemove: () => void
}

const FilterChip = ({
  filter, config, context, size = "md", onChange, onRemove,
}: Props) => {
  const tokens = CHIP_SIZES[size]

  return (
    <Flex
      h={tokens.h}
      borderRadius="md"
      borderWidth={1}
      borderColor="gray.300"
      alignItems="stretch"
      overflow="hidden"
      bg="white"
      minW={0}
      data-testid="filter-chip"
    >
      {/* Field label */}
      <Flex align="center" px={tokens.px} gap={tokens.labelGap} flexShrink={0} minW={0}>
        <Box display="inline-flex" color="gray.600" flexShrink={0}>{config.icon}</Box>
        <Text
          as="span"
          maxW={tokens.labelMaxW}
          minW={0}
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          fontSize={tokens.fontSize}
          fontWeight="semibold"
          color="gray.700"
          title={config.label}
        >
          {config.label}
        </Text>
      </Flex>
      <Divider orientation="vertical" />

      <FilterChipOperator
        filter={filter}
        config={config}
        size={size}
        onChange={(operator) => onChange({ operator })}
      />
      <Divider orientation="vertical" />

      <Box minW={0} maxW={tokens.valueMaxW} h="100%" display="flex" alignItems="stretch">
        <FilterChipValue
          filter={filter}
          config={config}
          context={context}
          size={size}
          onChange={(values) => onChange({ values })}
          onRemove={onRemove}
        />
      </Box>
      <Divider orientation="vertical" />

      <IconButton
        aria-label="Remove filter"
        icon={<Icon icon="times" size={tokens.removeIconSize} color="gray.500" />}
        variant="ghost"
        size={tokens.removeBtnSize}
        h="100%"
        w={tokens.h}
        minW={tokens.h}
        borderRadius={0}
        onClick={onRemove}
      />
    </Flex>
  )
}

export default FilterChip
